/**
 * CORE-1753 — сигнал `app.opened` для вошедшего человека (см.
 * `services/appOpenedBeacon`). Стоит один раз, в корне приложения.
 *
 * Только при согласии на данные ИИ (CORE-896). Приветствие ложится в чат
 * Агапеллы, а у того, кто согласия не давал, этот чат открывается экраном
 * согласия, а не перепиской: отметка непрочитанного вела бы к сообщению,
 * которого не видно. Веб-панель шлёт `chat.opened` тоже только после согласия
 * — здесь то же правило. «Ещё не прочитано» не считается согласием: пока
 * статус `loading`, сигнала нет, он уйдёт, как только статус станет `granted`.
 *
 * Пользователь берётся аргументами, а не из `useAuth`: хук и так вызывается
 * там, где авторизация уже прочитана, — так же устроены соседние
 * `useCookieConsentSync` и `useShareLinkAttribution`.
 */
import { useEffect } from 'react';
import { AppState, Platform } from 'react-native';
import { useAiDataConsent } from './useAiDataConsent';
import { emitProductEvent, ProductEvents } from '../services/productEvents';
import { storage } from '../utils/storage';
import {
  createAppOpenedBeacon,
  nativeForegroundSource,
  startAppOpenedBeacon,
  webForegroundSource,
  type ForegroundSource,
  type PageHideTargetLike,
  type VisibilityDocumentLike,
} from '../services/appOpenedBeacon';

// Один на приложение: предел частоты и отметка ухода должны пережить
// перемонтирование корня и смену статуса согласия, иначе каждое из них давало
// бы лишний сигнал или теряло длину ухода.
const beacon = createAppOpenedBeacon({
  send: (userId, trigger, awayMs) => {
    emitProductEvent(ProductEvents.appOpened, {
      userId,
      props: {
        trigger,
        platform: Platform.OS,
        // CORE-1753 — сколько приложения не было на экране, в секундах. Нет
        // отметки — нет и поля: сервер посчитает перерыв по своей.
        ...(awayMs === null ? {} : { away_s: Math.round(awayMs / 1000) }),
      },
    });
  },
  store: {
    read: () => storage.getAppLeftMark(),
    write: (mark) => storage.setAppLeftMark(mark),
  },
});

function foregroundSource(): ForegroundSource | null {
  if (Platform.OS === 'web') {
    return webForegroundSource(
      typeof document === 'undefined' ? null : (document as unknown as VisibilityDocumentLike),
      typeof window === 'undefined' ? null : (window as unknown as PageHideTargetLike),
    );
  }
  return nativeForegroundSource(AppState);
}

export function useAppOpenedBeacon(
  userId: string | null | undefined,
  isAuthenticated: boolean,
  isLoading: boolean,
  /**
   * Админ смотрит приложение от имени человека («войти как»). Это не его
   * возвращение: засчитать его значило бы поздороваться с человеком, которого
   * нет, и отнять приветствие у его настоящего захода — тот придёт внутри
   * трёх часов от подставного и получит тишину.
   */
  impersonating: boolean,
): void {
  const bareId = String(userId ?? '').replace(/^user_/, '');
  const signedIn = isAuthenticated && !isLoading && !impersonating && !!bareId;
  const { status: consent } = useAiDataConsent(signedIn ? bareId : '');
  const allowed = signedIn && consent === 'granted';

  useEffect(() => {
    if (!allowed) return undefined;
    const source = foregroundSource();
    if (!source) return undefined;
    return startAppOpenedBeacon({ userId: bareId, source, beacon });
  }, [allowed, bareId]);
}
